import { supabase } from "@/integrations/supabase/client";

export interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_type: 'string' | 'number' | 'boolean' | 'json';
  description: string;
  created_at: string;
  updated_at: string;
}

export const getSystemSettings = async (): Promise<Record<string, string | number | boolean | null>> => {
  const { data, error } = await (supabase as any)
    .from('system_settings')
    .select('*')
    .order('setting_key');

  if (error) {
    console.error('Error fetching system settings:', error);
    throw error;
  }

  const settings: Record<string, string | number | boolean | null> = {};
  (data as SystemSetting[])?.forEach((setting) => {
    let value: string | number | boolean | null = setting.setting_value;

    switch (setting.setting_type) {
      case 'number':
        value = parseFloat(value) || 0;
        break;
      case 'boolean':
        value = value === 'true';
        break;
      case 'json':
        try {
          value = JSON.parse(value);
        } catch {
          value = null;
        }
        break;
    }

    settings[setting.setting_key] = value;
  });

  return settings;
};

export const updateSystemSetting = async (key: string, value: string | number | boolean, type: string = 'string'): Promise<void> => {
  let stringValue = String(value);
  if (type === 'json') {
    stringValue = JSON.stringify(value);
  }

  const { error } = await (supabase as any)
    .from('system_settings')
    .upsert({
      setting_key: key,
      setting_value: stringValue,
      setting_type: type,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'setting_key'
    });

  if (error) {
    console.error('Error updating system setting:', error);
    throw error;
  }
};

export const updateMultipleSettings = async (settings: Array<{key: string, value: string | number | boolean, type: string}>): Promise<void> => {
  const updates = settings.map(setting => ({
    setting_key: setting.key,
    setting_value: setting.type === 'json' ? JSON.stringify(setting.value) : String(setting.value),
    setting_type: setting.type,
    updated_at: new Date().toISOString()
  }));

  const { error } = await (supabase as any)
    .from('system_settings')
    .upsert(updates, {
      onConflict: 'setting_key'
    });

  if (error) {
    console.error('Error updating multiple settings:', error);
    throw error;
  }
};
